import { useMemo } from "react";
import { useParams, Link } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { renderBatchLabel } from "@/lib/formatters";
import { useBatchTrail } from "../hooks/useBatchTrail";
import { StepDurationBar } from "../components/StepDurationBar";
import "./BatchTrailPage.css";

/**
 * Phase 18 WU-18.F4 — batch drill-through at
 * `/analytics/batches/:id/trail`.
 *
 * Sections:
 *   1. Header — batch id, name, job type, doc count, SLA chip, total duration.
 *   2. Timeline swim-lane — vertical list of AuditEntry rows for all docs
 *      in the batch (ingestion, locks, steps, exports, check-ins,
 *      rejections). Actions render as chips.
 *   3. Doc table — every doc, completedAt, duration, primary operator,
 *      SLA chip, correction count. Doc id links to DocumentTrailPage.
 *   4. StepDurationBar — aggregate step durations from batchStats.stepDurations.
 *
 * 410 Gone + inline permission panel match DocumentTrailPage.
 */
export function BatchTrailPage() {
  const { id } = useParams();
  const batchId = id ?? "";
  const { user } = useAuth();

  const trail = useBatchTrail(batchId);

  const permissions = user?.permissions ?? [];
  const hasPermission =
    permissions.includes("analytics.view_own") ||
    permissions.includes("analytics.view_others");

  const stepDurations = useMemo<Record<string, number>>(() => {
    const raw = trail.data?.batchStats?.stepDurations;
    if (!raw || typeof raw !== "object") return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === "number" && v > 0) out[k] = v;
    }
    return out;
  }, [trail.data]);

  if (user && !hasPermission) {
    return (
      <div className="batch-trail">
        <header className="batch-trail__header">
          <h1 className="batch-trail__title">Batch Trail</h1>
        </header>
        <div className="batch-trail__forbidden" data-testid="batch-trail-forbidden">
          You need <strong>analytics.view_own</strong> or
          <strong> analytics.view_others</strong> permission to view trails.
        </div>
      </div>
    );
  }

  if (trail.isLoading) {
    return (
      <div className="batch-trail">
        <header className="batch-trail__header">
          <h1 className="batch-trail__title">{batchId}</h1>
        </header>
        <div className="analytics-loading" data-testid="batch-trail-loading">Loading…</div>
      </div>
    );
  }

  if (trail.error?.status === 410) {
    return (
      <div className="batch-trail">
        <header className="batch-trail__header">
          <h1 className="batch-trail__title">{batchId}</h1>
        </header>
        <div className="batch-trail__gone" data-testid="batch-trail-gone">
          <strong>Detail retained 90 days.</strong>
          <span>
            {" "}This batch's trail has been evicted. A monthly summary is
            still available in the aggregate analytics views.
          </span>
        </div>
      </div>
    );
  }

  if (trail.error) {
    return (
      <div className="batch-trail">
        <header className="batch-trail__header">
          <h1 className="batch-trail__title">{batchId}</h1>
        </header>
        <div className="analytics-error" data-testid="batch-trail-error">
          {trail.error.message}
        </div>
      </div>
    );
  }

  const data = trail.data;
  if (!data) return null;
  const { batch, batchStats, timeline, docs } = data;

  const totalDurationMs = batchStats?.totalDurationMs ?? 0;
  const slaTargetMs = batchStats?.slaTargetMs ?? null;
  const slaMet = batchStats?.slaMet ?? null;
  const slaChipClass = slaMet === true
    ? "batch-trail__chip batch-trail__chip--met"
    : slaMet === false
      ? "batch-trail__chip batch-trail__chip--missed"
      : "batch-trail__chip batch-trail__chip--unknown";
  const slaChipLabel = slaMet === true ? "SLA met" : slaMet === false ? "SLA missed" : "SLA n/a";

  return (
    <div className="batch-trail">
      <header className="batch-trail__header">
        <Link to="/admin/analytics" className="batch-trail__back">
          ← Analytics
        </Link>
        {/* WU-7 — title is the structured displayId when available, falling
            back to `B-<cuid-prefix>` for legacy rows. Free-form name moves
            into the meta row alongside the other muted attributes. */}
        <h1
          className="batch-trail__title"
          data-testid="batch-trail-title"
        >
          {/* R-167 WU-7 — customDisplayLabel ?? displayId ?? cuid. */}
          {renderBatchLabel(batch)}
        </h1>
        <div className="batch-trail__meta">
          <span>Name: {batch.name || "—"}</span>
          <span>Job Type: {batchStats?.jobTypeId ?? "—"}</span>
          <span>Docs: {docs.length.toLocaleString()}</span>
          <span>Duration: {formatDuration(totalDurationMs)}</span>
          <span className={slaChipClass}>{slaChipLabel}</span>
        </div>
      </header>

      <section className="batch-trail__section">
        <h2 className="analytics-section-title">Step Durations</h2>
        <div data-testid="batch-trail-duration-bar">
          <StepDurationBar
            stepDurations={stepDurations}
            slaTargetMs={slaTargetMs}
            totalDurationMs={totalDurationMs}
          />
        </div>
      </section>

      <section className="batch-trail__section">
        <h2 className="analytics-section-title">Timeline</h2>
        <div className="batch-trail__timeline" data-testid="batch-trail-timeline">
          {timeline.length === 0 ? (
            <div className="batch-trail__timeline-empty">
              No timeline events recorded.
            </div>
          ) : (
            <ol className="batch-trail__timeline-list">
              {timeline.map((t, i) => (
                <li
                  key={`${t.at}-${i}`}
                  className="batch-trail__timeline-row"
                  data-testid={`batch-trail-timeline-row-${i}`}
                >
                  <span className="batch-trail__timeline-time">
                    {formatTimestamp(t.at)}
                  </span>
                  <span
                    className={`batch-trail__action-chip batch-trail__action-chip--${actionClass(t.action)}`}
                  >
                    {t.action}
                  </span>
                  <span className="batch-trail__timeline-by">by {t.by}</span>
                  <span className="batch-trail__timeline-meta">{summarizeMetadata(t.metadata)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      <section className="batch-trail__section">
        <h2 className="analytics-section-title">Documents</h2>
        <div data-testid="batch-trail-docs">
          {docs.length === 0 ? (
            <div className="batch-trail__docs-empty">No documents recorded.</div>
          ) : (
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Completed</th>
                  <th>Duration</th>
                  <th>Operator</th>
                  <th>Corrections</th>
                  <th>Keystrokes</th>
                  <th>SLA</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => {
                  const chipClass =
                    d.slaMet === true
                      ? "batch-trail__chip batch-trail__chip--met"
                      : d.slaMet === false
                        ? "batch-trail__chip batch-trail__chip--missed"
                        : "batch-trail__chip batch-trail__chip--unknown";
                  const chipLabel = d.slaMet === true ? "met" : d.slaMet === false ? "missed" : "n/a";
                  return (
                    <tr
                      key={d.documentId}
                      data-testid={`batch-trail-doc-row-${d.documentId}`}
                    >
                      <td>
                        <Link
                          to={`/analytics/documents/${d.documentId}/trail`}
                          className="analytics-link"
                        >
                          {d.documentId}
                        </Link>
                      </td>
                      <td>{formatTimestamp(d.completedAt)}</td>
                      <td>{formatDuration(d.totalDurationMs)}</td>
                      <td>{d.primaryOperator ?? "—"}</td>
                      <td>{d.correctionCount.toLocaleString()}</td>
                      <td>{d.keystrokeCount.toLocaleString()}</td>
                      <td><span className={chipClass}>{chipLabel}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)}m`;
  return `${(ms / 3_600_000).toFixed(2)}h`;
}

function formatTimestamp(iso: string): string {
  return iso.slice(0, 16).replace("T", " ");
}

// Small mapping from action verbs to a semantic class for chip coloring.
function actionClass(action: string): string {
  if (action.startsWith("batch.")) return "batch";
  if (action.startsWith("field.")) return "field";
  if (action.startsWith("export")) return "export";
  if (action.includes("reject")) return "reject";
  if (action.includes("lock")) return "lock";
  if (action.includes("checkin")) return "checkin";
  if (action.includes("step")) return "step";
  return "default";
}

function summarizeMetadata(metadata: unknown): string {
  if (metadata == null) return "";
  if (typeof metadata !== "object") return String(metadata);
  const entries = Object.entries(metadata as Record<string, unknown>);
  if (entries.length === 0) return "";
  return entries
    .slice(0, 3)
    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(" · ");
}
